/**
 * Parse CSV text into rows of string fields.
 * Matches server import behavior: quoted fields, commas inside quotes, escaped `""`,
 * and newlines inside quoted fields (Windows / Unix line endings).
 */
export function parseCSV(csvData: string): string[][] {
  // Handle different line endings (Windows \r\n, Unix \n, Mac \r)
  const normalizedData = csvData.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalizedData.split('\n');
  const result: string[][] = [];

  let currentLine: string[] = [];
  let currentField = '';
  let inQuotes = false;

  for (const line of lines) {
    // Skip completely empty lines
    if (!line.trim() && !inQuotes) {
      continue;
    }

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = i + 1 < line.length ? line[i + 1] : '';

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote (double quote)
          currentField += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator
        currentLine.push(currentField);
        currentField = '';
      } else {
        currentField += char;
      }
    }

    // If we're not in quotes, this line is complete
    if (!inQuotes) {
      // Add the last field
      currentLine.push(currentField);
      // Only add non-empty lines (at least header should have content)
      if (currentLine.length > 0 && (result.length === 0 || currentLine.some((f) => f.trim()))) {
        result.push(currentLine);
      }
      currentLine = [];
      currentField = '';
    } else {
      // Line break inside quoted field - add newline to current field
      currentField += '\n';
    }
  }

  // Handle case where file ends while still in quotes
  if (inQuotes || currentField) {
    currentLine.push(currentField);
    if (currentLine.length > 0 && currentLine.some((f) => f.trim())) {
      result.push(currentLine);
    }
  }

  return result;
}
